// Das Feld, wie der Client es sieht.
//
// Der Client **berechnet** das Feld aus der Weltsaat und haelt daneben einen
// Abweichungsspeicher — genau wie der Server (`pve/overlay.py`). Ueber die
// Leitung geht deshalb nur, worin die Wirklichkeit vom berechneten Feld
// abweicht.
//
// Zwei Betriebsarten, ein Datenmodell:
//
// * **Solo** — der Client ist seine eigene Autoritaet und schreibt selbst in
//   den Abweichungsspeicher.
// * **Online** — der Server schreibt, der Client hoert zu. Er sagt dann *nie*
//   selbst „der Brocken ist kaputt": zwei Autoritaeten waeren zwei Wahrheiten,
//   und die zweite gewinnt nur beim Schummeln.

import { balancing } from '../data/balancing';
import { chunkCoords, chunkRocks, fragments, type Rock } from './field';

export interface RockDelta {
  hp: number;
  respawnAt: number | null;
  /**
   * Weltzeit des letzten Treffers — **nur Darstellung**, im Muster von
   * `Hostile.shotT`.
   *
   * Der Lebensbalken blendet danach aus, und der Zeitpunkt muss deshalb im
   * **mutierenden** Pfad entstehen. Ihn im Zeichenpfad zu setzen haenge ihn an
   * die Bildrate (Gate FG-17).
   */
  hitAt: number;
}

export interface DamageResult {
  hp: number;
  destroyed: boolean;
  fragments: Rock[];
}

const FRAGMENT_LIFETIME = 26;

/** „Noch nie getroffen" — liegt beliebig weit in der Vergangenheit. */
const NEVER = -Infinity;

export class World {
  private readonly chunks = new Map<string, Rock[]>();
  private readonly deltas = new Map<string, RockDelta>();
  private readonly fragmentSets = new Map<string, { rocks: Rock[]; expiresAt: number }>();
  /** Kurzweg von der ID zum Brocken — fuer Server-Abweichungen. */
  private readonly byId = new Map<string, Rock>();

  time = 0;

  constructor(
    public seed: number,
    /** Solo: der Client entscheidet selbst. Online: nur der Server schreibt. */
    public authoritative: boolean,
    /**
     * Die Ära des Feldes — die Entsprechung von `FieldOverlay.era`.
     *
     * Sie steht **hier** und nicht als Parameter jeder Abfrage: ein Feld hat
     * genau eine Ära, und wer die Weltgenerierung befragt, hält das Feld
     * ohnehin schon. Ein Aufruf, der sie vergäße, fiele still auf Akt I
     * zurück — der Spieler schösse dann ins Leere, ohne Fehlermeldung.
     */
    public era: number = balancing().eraFirst,
  ) {}

  reset(seed: number, authoritative: boolean, era: number = balancing().eraFirst): void {
    this.chunks.clear();
    this.deltas.clear();
    this.fragmentSets.clear();
    this.byId.clear();
    this.seed = seed;
    this.authoritative = authoritative;
    this.era = era;
    this.time = 0;
  }

  private key(cx: number, cy: number): string {
    return `${cx},${cy}`;
  }

  chunkAt(cx: number, cy: number): Rock[] {
    const key = this.key(cx, cy);
    let rocks = this.chunks.get(key);
    if (!rocks) {
      rocks = chunkRocks(cx, cy, this.seed, this.era);
      this.chunks.set(key, rocks);
      for (const rock of rocks) this.byId.set(rock.id, rock);
    }
    return rocks;
  }

  /** Chunks um (x, y) laden, weit entfernte verwerfen, Bruchstuecke altern. */
  update(dt: number, x: number, y: number): void {
    this.time += dt;
    const radius = balancing().world.loadRadius;
    const [ccx, ccy] = chunkCoords(x, y);

    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) this.chunkAt(ccx + dx, ccy + dy);
    }

    for (const key of [...this.chunks.keys()]) {
      const [cx, cy] = key.split(',').map(Number);
      if (Math.abs(cx - ccx) > radius + 1 || Math.abs(cy - ccy) > radius + 1) {
        for (const rock of this.chunks.get(key) ?? []) this.byId.delete(rock.id);
        this.chunks.delete(key);
      }
    }

    for (const [id, set] of this.fragmentSets) {
      if (set.expiresAt <= this.time) {
        for (const piece of set.rocks) { this.deltas.delete(piece.id); this.byId.delete(piece.id); }
        this.fragmentSets.delete(id);
      }
    }

    for (const [id, delta] of this.deltas) {
      if (delta.respawnAt !== null && this.time >= delta.respawnAt) this.deltas.delete(id);
    }
  }

  // ── Lesen ──────────────────────────────────────────────────────────────────
  rockById(id: string): Rock | undefined {
    return this.byId.get(id);
  }

  delta(id: string): RockDelta | undefined {
    return this.deltas.get(id);
  }

  hpOf(rock: Rock): number {
    return this.deltas.get(rock.id)?.hp ?? rock.maxHp;
  }

  /**
   * Sekunden seit dem letzten Treffer. Ohne Abweichung `Infinity` — der Brocken
   * ist unberuehrt.
   *
   * Rein und ohne Nebenwirkung: der Zeichenpfad fragt, er schreibt nicht.
   */
  hitAgeOf(rock: Rock): number {
    const delta = this.deltas.get(rock.id);
    return delta ? this.time - delta.hitAt : Infinity;
  }

  isAlive(rock: Rock): boolean {
    return this.isAliveId(rock.id);
  }

  /**
   * Dasselbe, aber **nur aus der ID** — für ein Ziel, das kein Brocken sein muss.
   *
   * Eine Lenkwaffe hält ein schmales `GuidedTarget` (`sim/guided.ts`) und weiß
   * nicht, ob dahinter ein Brocken oder ein Gegner steckt. Sie fragt beide
   * Felder; wer die ID nicht kennt, antwortet für sein Feld.
   */
  isAliveId(id: string): boolean {
    const delta = this.deltas.get(id);
    return delta === undefined || delta.respawnAt === null;
  }

  /**
   * Alle lebenden Brocken im Umkreis. `out` wird **wiederverwendet** —
   * ein neues Array je Aufruf waere 60-mal pro Sekunde Muell (Gate FG-14).
   */
  queryCircle(x: number, y: number, radius: number, out: Rock[]): Rock[] {
    out.length = 0;
    const size = balancing().world.chunk;
    const r2 = radius * radius;
    const c0x = Math.floor((x - radius) / size);
    const c1x = Math.floor((x + radius) / size);
    const c0y = Math.floor((y - radius) / size);
    const c1y = Math.floor((y + radius) / size);
    for (let cy = c0y; cy <= c1y; cy++) {
      for (let cx = c0x; cx <= c1x; cx++) {
        const rocks = this.chunks.get(this.key(cx, cy));
        if (!rocks) continue;
        for (const rock of rocks) {
          if (!this.isAlive(rock)) continue;
          const dx = rock.x - x;
          const dy = rock.y - y;
          if (dx * dx + dy * dy <= r2 + rock.r * rock.r) out.push(rock);
        }
      }
    }
    for (const set of this.fragmentSets.values()) {
      for (const piece of set.rocks) {
        if (!this.isAlive(piece)) continue;
        const dx = piece.x - x;
        const dy = piece.y - y;
        if (dx * dx + dy * dy <= r2 + piece.r * piece.r) out.push(piece);
      }
    }
    return out;
  }

  /** Lebende Brocken im Sichtrechteck (fuers Zeichnen). */
  forEachInRect(x0: number, y0: number, x1: number, y1: number, fn: (rock: Rock) => void): void {
    const size = balancing().world.chunk;
    for (let cy = Math.floor(y0 / size); cy <= Math.floor(y1 / size); cy++) {
      for (let cx = Math.floor(x0 / size); cx <= Math.floor(x1 / size); cx++) {
        for (const rock of this.chunks.get(this.key(cx, cy)) ?? []) {
          if (!this.isAlive(rock)) continue;
          if (rock.x + rock.r < x0 || rock.x - rock.r > x1) continue;
          if (rock.y + rock.r < y0 || rock.y - rock.r > y1) continue;
          fn(rock);
        }
      }
    }
    for (const set of this.fragmentSets.values()) {
      for (const piece of set.rocks) {
        if (!this.isAlive(piece)) continue;
        if (piece.x + piece.r < x0 || piece.x - piece.r > x1) continue;
        if (piece.y + piece.r < y0 || piece.y - piece.r > y1) continue;
        fn(piece);
      }
    }
  }

  // ── Schreiben ──────────────────────────────────────────────────────────────
  /**
   * Schaden im Solo-Modus. Online wirft das — dort schreibt nur der Server.
   *
   * Bewusst ein Fehler statt eines stillen `return`: ein Aufrufer, der online
   * selbst Schaden bucht, hat einen Denkfehler, keinen Sonderfall.
   */
  damage(rock: Rock, amount: number, splitBonus: number): DamageResult {
    if (!this.authoritative) {
      throw new Error('Online schreibt nur der Server in den Abweichungsspeicher.');
    }
    return this.applyDamage(rock, amount, splitBonus);
  }

  /**
   * Eine Abweichung vom Server uebernehmen.
   *
   * `pieces` ist die Zahl der Bruchstuecke, die der **Server** gemeldet hat —
   * nicht ein Splitterbonus. Online raet der Client die Anzahl nie: sie haengt
   * am `splitBonus` desjenigen, der den Brocken zerlegt hat, und den kennt er
   * nicht. Genau daran fehlten mit gekaufter Splitterladung online Stuecke.
   *
   * @param fresh ob die Abweichung ein **frischer** Treffer ist.
   *
   * Der Bestandsabzug beim Aufschliessen (`snapshot`) meldet dieselben
   * Abweichungen wie ein Treffer, nur beliebig alt: dort mitzuzaehlen liesse
   * beim Verbinden und bei jedem Sektorwechsel schlagartig jeden angeschlagenen
   * Brocken des Sektors aufleuchten. Ein alter Abzug ueberschreibt umgekehrt
   * auch keinen frischen Treffer.
   */
  applyServerDelta(
    id: string, hp: number, destroyed: boolean, pieces: number, fresh = true,
  ): Rock | undefined {
    const rock = this.byId.get(id);
    if (!rock) return undefined;
    if (destroyed) {
      this.kill(rock, pieces);
    } else {
      const known = this.deltas.get(id)?.hitAt ?? NEVER;
      this.deltas.set(id, { hp, respawnAt: null, hitAt: fresh ? this.time : known });
    }
    return rock;
  }

  /** Bruchstuecke einhaengen. `ttl` ist ihre Restlebensdauer in Sekunden. */
  adoptFragments(parentId: string, pieces: Rock[], ttl = FRAGMENT_LIFETIME): void {
    if (!pieces.length) return;
    this.fragmentSets.set(parentId, { rocks: pieces, expiresAt: this.time + ttl });
    for (const piece of pieces) this.byId.set(piece.id, piece);
  }

  /**
   * Einen vom Server gemeldeten Bruchstuecksatz nachrechnen und einhaengen.
   *
   * Uebertragen werden nur Eltern-ID, Anzahl und Restlaufzeit — die Stuecke
   * selbst sind daraus berechenbar und beidseitig bitgleich (Gate FG-30).
   *
   * Den Eltern-Brocken sucht die Methode notfalls ueber seinen Chunk: beim
   * Aufschliessen kann er laengst zerstoert sein, seine Kachel aber noch
   * ungeladen. Findet sie ihn nicht, liegt der Satz ausserhalb des geladenen
   * Feldes und ist ohnehin unsichtbar.
   */
  adoptServerFragments(parentId: string, count: number, ttl = FRAGMENT_LIFETIME): Rock[] {
    if (count <= 0) return [];
    let parent = this.byId.get(parentId);
    if (!parent) {
      const [cx, cy] = parentId.split('#')[0].split(':').map(Number);
      if (!Number.isFinite(cx) || !Number.isFinite(cy)) return [];
      parent = this.chunkAt(cx, cy).find((rock) => rock.id === parentId);
    }
    if (!parent) return [];
    const pieces = fragments(parent, count, this.seed, this.era);
    this.adoptFragments(parentId, pieces, ttl);
    return pieces;
  }

  private applyDamage(rock: Rock, amount: number, splitBonus: number): DamageResult {
    const current = this.deltas.get(rock.id);
    if (current && current.respawnAt !== null) return { hp: 0, destroyed: false, fragments: [] };

    const hp = (current?.hp ?? rock.maxHp) - amount;
    if (hp > 0) {
      this.deltas.set(rock.id, { hp, respawnAt: null, hitAt: this.time });
      return { hp, destroyed: false, fragments: [] };
    }
    const pieces = rock.split + Math.max(0, splitBonus);
    return { hp: 0, destroyed: true, fragments: this.kill(rock, pieces) };
  }

  /** @param pieces die **endgueltige** Stueckzahl, nicht der Bonus darauf. */
  private kill(rock: Rock, pieces: number): Rock[] {
    const isFragment = rock.id.includes('#');
    this.deltas.set(rock.id, {
      hp: 0,
      // Bruchstuecke wachsen nicht nach — sie waren nie Teil des Feldes. Ihr
      // Zeitpunkt liegt hinter dem Ablauf ihres Satzes, der sie mitsamt
      // Abweichung aufraeumt; wortgleich in `pve/overlay.py`.
      respawnAt: this.time + (isFragment ? FRAGMENT_LIFETIME : balancing().world.respawnDelay),
      hitAt: this.time,
    });

    if (isFragment || rock.r <= 20 || pieces <= 0) return [];
    const parts = fragments(rock, pieces, this.seed, this.era);
    this.adoptFragments(rock.id, parts);
    return parts;
  }

  get rockCount(): number {
    let n = 0;
    for (const rocks of this.chunks.values()) n += rocks.length;
    return n;
  }
}
